import React, { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { participantsApi } from "../api/participants";
import { qk } from "../../../../lib/queryKeys";
import { ROUTES } from "../../../../shared/utils";

const STATUS_OPTIONS = ["Active", "Pending", "On hold", "Completed"];
const FLAGGED_OPTIONS = ["None", "Needs follow-up", "Escalated"];
const statusChipStyles: Record<string, { bg: string; text: string }> = {
  Active: { bg: "#dcfce7", text: "#166534" },
  Pending: { bg: "#fef9c3", text: "#854d0e" },
  "On hold": { bg: "#fee2e2", text: "#991b1b" },
  Completed: { bg: "#e0f2fe", text: "#075985" },
};

const getStatusChipSx = (status?: string | null) => {
  if (!status) return undefined;
  const palette = statusChipStyles[status];
  if (!palette) return undefined;
  return { backgroundColor: palette.bg, color: palette.text, fontWeight: 600 };
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

export const Participants: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [flagged, setFlagged] = useState("");
  const [whoopReceived, setWhoopReceived] = useState<"" | "true" | "false">("");
  const [usingOwnDevice, setUsingOwnDevice] = useState<"" | "true" | "false">("");

  const queryParams = useMemo(
    () => ({
      search: search || undefined,
      status: status || undefined,
      flagged: flagged === "None" ? "" : flagged || undefined,
      whoop_received:
        whoopReceived === ""
          ? undefined
          : whoopReceived === "true",
      using_own_device:
        usingOwnDevice === ""
          ? undefined
          : usingOwnDevice === "true",
    }),
    [search, status, flagged, whoopReceived, usingOwnDevice]
  );

  const listQuery = useQuery({
    queryKey: qk.participants(queryParams),
    queryFn: () => participantsApi.list(queryParams),
  });

  return (
    <Box>
      <Stack spacing={3}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <Box flex={1}>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Participants
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage participant records, device readiness, and programme tracking.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate(`${ROUTES.PARTICIPANTS}/new`)}
          >
            New participant
          </Button>
        </Stack>

        <Paper sx={{ p: 3 }} elevation={2}>
          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight={600}>
              Filters
            </Typography>
            <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
              <TextField
                label="Search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Name, email, or company"
                fullWidth
              />
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Flagged</InputLabel>
                <Select
                  label="Flagged"
                  value={flagged}
                  onChange={(event) => setFlagged(event.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {FLAGGED_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>WHOOP received</InputLabel>
                <Select
                  label="WHOOP received"
                  value={whoopReceived}
                  onChange={(event) =>
                    setWhoopReceived(event.target.value as "" | "true" | "false")
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Using own device</InputLabel>
                <Select
                  label="Using own device"
                  value={usingOwnDevice}
                  onChange={(event) =>
                    setUsingOwnDevice(event.target.value as "" | "true" | "false")
                  }
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ p: 0 }} elevation={2}>
          {listQuery.isLoading ? (
            <Box display="flex" alignItems="center" gap={1} p={3}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading participants...</Typography>
            </Box>
          ) : listQuery.isError ? (
            <Alert severity="error" sx={{ m: 2 }}>
              Failed to load participants.
            </Alert>
          ) : listQuery.data?.data?.length ? (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Participant</TableCell>
                  <TableCell>Linked user</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Flags</TableCell>
                  <TableCell>Device status</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listQuery.data.data.map((participant) => {
                  const name = `${participant.first_name} ${participant.surname}`.trim();
                  const initials = `${participant.first_name?.[0] ?? ""}${participant.surname?.[0] ?? ""}`
                    .toUpperCase()
                    .slice(0, 2);
                  return (
                    <TableRow
                      key={participant.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(`${ROUTES.PARTICIPANTS}/${participant.id}`)
                      }
                    >
                      <TableCell>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Avatar>{initials || "P"}</Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {name || "-"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {participant.email || "No email"}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {participant.user_email || "Not linked"}
                        </Typography>
                        {(participant.user_first_name || participant.user_last_name) && (
                          <Typography variant="caption" color="text.secondary">
                            {participant.user_first_name} {participant.user_last_name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{participant.company || "-"}</TableCell>
                      <TableCell>
                        {participant.status ? (
                          <Chip
                            label={participant.status}
                            size="small"
                            sx={getStatusChipSx(participant.status)}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {participant.flagged ? (
                            <Chip label={participant.flagged} size="small" />
                          ) : (
                            <Chip label="No flags" size="small" variant="outlined" />
                          )}
                          {participant.color_tag && (
                            <Chip label={participant.color_tag} size="small" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip
                            label="WHOOP"
                            size="small"
                            variant={participant.whoop_received ? "filled" : "outlined"}
                            sx={
                              participant.whoop_received
                                ? { backgroundColor: "#ecfccb", color: "#365314", fontWeight: 600 }
                                : { color: "text.secondary" }
                            }
                          />
                          <Chip
                            label="Own device"
                            size="small"
                            variant={participant.using_own_device ? "filled" : "outlined"}
                            sx={
                              participant.using_own_device
                                ? { backgroundColor: "#dbeafe", color: "#1e3a8a", fontWeight: 600 }
                                : { color: "text.secondary" }
                            }
                          />
                        </Stack>
                      </TableCell>
                      <TableCell>{formatDateTime(participant.updated_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <Alert severity="info" sx={{ m: 2 }}>
              No participants found.
            </Alert>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};
